import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Button,
  Input,
  Badge,
  Spinner,
  UncontrolledTooltip
} from 'reactstrap';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';

const STATUS_COLORS = {
  running: 'info',
  succeeded: 'success',
  failed: 'danger'
};

const RecentJobs = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(''); // '', running, succeeded, failed
  const [query, setQuery] = useState(''); // search by job_name/triggered_by
  const [limit, setLimit] = useState(20);
  const [autoRefresh, setAutoRefresh] = useState(true);
  // Initialize active tab from URL (?tab=)
  const initialTab = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = sp.get('tab');
      return t || 'fmp_collection';
    } catch {
      return 'fmp_collection';
    }
  })();
  const [activeType, setActiveType] = useState(initialTab);
  const [hideScoring, setHideScoring] = useState(true);
  const [enabledMap, setEnabledMap] = useState(null);
  const [fmpInfo, setFmpInfo] = useState(null);

  const fetchJobs = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE_URL}/api/jobs/executions`);
      url.searchParams.set('limit', String(limit));
      if (status) url.searchParams.set('status', status);
      if (activeType && activeType !== 'All') {
        url.searchParams.set('job_type', activeType);
        if (activeType === 'fmp_collection') {
          url.searchParams.set('job_name_like', 'FMP Collect %');
        }
      }
      const res = await fetch(url.toString());
      const json = await res.json();
      const jobsArray = Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json?.executions)
        ? json.executions
        : Array.isArray(json)
        ? json
        : [];
      setJobs(jobsArray);
    } catch (e) {
      setError('Failed to load recent jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [status, limit, activeType]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(fetchJobs, 10000);
    return () => clearInterval(t);
  }, [autoRefresh, activeType, status, limit]);

  // Fetch enabled status once and when API base changes
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/jobs/enabled-status`);
        const json = await res.json();
        if (json?.success) setEnabledMap(json.data);
      } catch {}
      try {
        const res2 = await fetch(`${API_BASE_URL}/api/jobs/status`);
        const j2 = await res2.json();
        if (j2?.success) {
          setFmpInfo(j2.data?.fmpScheduler);
          if (j2.data?.enabled) setEnabledMap(j2.data.enabled);
        }
      } catch {}
    };
    load();
  }, [API_BASE_URL]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = hideScoring && activeType !== 'prediction_scoring'
      ? jobs.filter(j => j.job_type !== 'prediction_scoring')
      : jobs;
    if (!q) return base;
    return base.filter(j =>
      (j.job_name || '').toLowerCase().includes(q) ||
      (j.job_type || '').toLowerCase().includes(q) ||
      (j.triggered_by || '').toLowerCase().includes(q) ||
      (j.trigger_user || '').toLowerCase().includes(q)
    );
  }, [jobs, query]);

  // Determine job types present and default tab (prefer a non-scoring type if available)
  const typeOrder = ['fmp_collection', 'daily_predictions', '4h_predictions', '7d_predictions', '30d_predictions', '180d_predictions', 'daily_reports', 'daily_video', 'sentiment', 'prediction_scoring', 'other'];
  const typeLabels = {
    fmp_collection: 'FMP Data Collection',
    daily_predictions: 'Daily Predictions',
    '4h_predictions': '4h Predictions',
    '7d_predictions': '7d Predictions',
    '30d_predictions': '30d Predictions',
    '180d_predictions': '180d Predictions',
    daily_reports: 'Report Screenshots',
    daily_video: 'Daily Video',
    sentiment: 'Sentiment',
    prediction_scoring: 'Scoring',
    other: 'Other'
  };

  const normalizeType = (t, j) => {
    const s = (t || '').toLowerCase();
    const name = (j?.job_name || '').toLowerCase();
  if (name.startsWith('fmp collect ')) return 'fmp_collection';
  if (name === 'expiredpredictionscoring') return 'prediction_scoring';
  if (name === 'dailyfeargreed') return 'sentiment';
    if (name === 'dailyreportscreenshots') return 'daily_reports';
    if (name === 'dailyvideoautomation') return 'daily_video';
  if (name === 'sevendaypredictions') return '7d_predictions';
  if (name === 'thirtydaypredictions') return '30d_predictions';
  if (name === 'oneeightdaypredictions' || name === 'oneeightydaypredictions') return '180d_predictions';
    if (s === 'daily_predictions' || s === 'long_term_predictions') return 'daily_predictions';
    if (s === '4h_predictions' || s === 'short_term_predictions') return '4h_predictions';
    if (s === 'daily_reports') return 'daily_reports';
    if (s === 'daily_video') return 'daily_video';
    if (s === 'prediction_scoring' || s === 'prediction_score' || s === 'prediciton_score' || s === 'expired_prediction_scoring') return 'prediction_scoring';
    return 'other';
  };

  const grouped = useMemo(() => {
    const map = new Map();
    // Seed with all known types so tabs always show
    for (const t of typeOrder) map.set(t, []);
    for (const j of filtered) {
      const key = normalizeType(j.job_type, j);
      map.get(key).push(j);
    }
    return map;
  }, [filtered]);

  // Ensure a valid tab is always selected and reflect into URL param
  useEffect(() => {
    if (!typeOrder.includes(activeType)) {
      setActiveType('daily_predictions');
      return;
    }
    try {
      const sp = new URLSearchParams(window.location.search);
      sp.set('tab', activeType);
      const url = `${window.location.pathname}?${sp.toString()}`;
      window.history.replaceState({}, '', url);
    } catch {}
  }, [activeType]);

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '');
  const formatSec = (s) => (s == null ? '' : `${s}s`);

  const renderRows = (rows) => (
    rows.map((j, idx) => (
      <tr key={`${j.id || j.job_id || idx}-${j.started_at || idx}`}>
        <td>
          <Badge color={STATUS_COLORS[j.status] || 'secondary'}>{(j.status || '').toUpperCase()}</Badge>
        </td>
        <td className="text-nowrap">{j.job_name || '-'}</td>
        <td>{j.job_type || '-'}</td>
        <td className="text-nowrap">{formatDate(j.started_at)}</td>
        <td className="text-nowrap">{formatDate(j.completed_at)}</td>
        <td>{formatSec(j.duration_seconds)}</td>
        <td className="text-nowrap">{j.triggered_by || j.trigger_user || '-'}</td>
        <td style={{maxWidth: 360}}>
          <small className="text-muted text-truncate d-inline-block" style={{maxWidth: 340}}>
            {typeof j.result_summary === 'string' ? j.result_summary : JSON.stringify(j.result_summary || {})}
          </small>
        </td>
      </tr>
    ))
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col>
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-sm-0">Recent Jobs</h4>
                <p className="text-muted mb-0">Live view of recent cron/manual job executions</p>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Input
                  type="select"
                  bsSize="sm"
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value) || 50)}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </Input>
                <Input
                  type="select"
                  bsSize="sm"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="running">Running</option>
                  <option value="succeeded">Succeeded</option>
                  <option value="failed">Failed</option>
                </Input>
                <Input
                  id="job-search"
                  placeholder="Search jobs/users"
                  bsSize="sm"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <Button id="refresh-btn" color="primary" size="sm" onClick={fetchJobs} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : 'Refresh'}
                </Button>
                <Button
                  id="auto-refresh-btn"
                  color={autoRefresh ? 'success' : 'secondary'}
                  size="sm"
                  onClick={() => setAutoRefresh(v => !v)}
                >
                  Auto {autoRefresh ? 'On' : 'Off'}
                </Button>
                <UncontrolledTooltip target="refresh-btn">Reload now</UncontrolledTooltip>
                <UncontrolledTooltip target="auto-refresh-btn">Toggle 10s auto-refresh</UncontrolledTooltip>
              </div>
            </div>
          </Col>
        </Row>

        {error && (
          <Row>
            <Col>
              <div className="alert alert-danger">{error}</div>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0">Latest Executions ({filtered.length})</h5>
                  <div className="d-flex align-items-center gap-3">
                    {fmpInfo && (
                      <div className="text-muted small">
                        FMP Scheduler: {fmpInfo.enabled ? <Badge color="success">Enabled</Badge> : <Badge color="secondary">Disabled</Badge>} {fmpInfo.running ? <Badge color="info" className="ms-1">Running</Badge> : null}
                      </div>
                    )}
                    <div className="form-check mb-0">
                      <Input id="hide-scoring" type="checkbox" className="form-check-input" checked={hideScoring} onChange={(e) => setHideScoring(e.target.checked)} />
                      <label className="form-check-label" htmlFor="hide-scoring">Hide Scoring</label>
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <Nav tabs>
                    {typeOrder.map((t) => {
                      const key = t;
                      const label = t === 'All' ? 'All' : (typeLabels[t] || t);
                      const count = (grouped.get(t)?.length || 0);
                      const enabled = enabledMap ? enabledMap[t] : undefined;
                      return (
                        <NavItem key={key}>
                          <NavLink
                            className={activeType === key ? 'active' : ''}
                            onClick={() => setActiveType(key)}
                            role="button"
                          >
                            {label} <Badge pill color="secondary" className="ms-1">{count}</Badge>{' '}
                            {enabled !== undefined && (
                              enabled ? <Badge pill color="success" className="ms-1">On</Badge> : <Badge pill color="secondary" className="ms-1">Off</Badge>
                            )}
                          </NavLink>
                        </NavItem>
                      );
                    })}
                  </Nav>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <TabContent activeTab={activeType}>
                    {typeOrder.map((t) => (
                      <TabPane tabId={t} key={t}>
                        <Table hover className="mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Status</th>
                              <th>Job</th>
                              <th>Type</th>
                              <th>Started</th>
                              <th>Completed</th>
                              <th>Duration</th>
                              <th>Triggered By</th>
                              <th>Summary</th>
                            </tr>
                          </thead>
                          <tbody>
                            {renderRows(grouped.get(t) || [])}
                            {!loading && (grouped.get(t)?.length || 0) === 0 && (
                              <tr><td colSpan="8" className="text-center text-muted py-4">No jobs for this type</td></tr>
                            )}
                            {loading && (
                              <tr><td colSpan="8" className="text-center py-4"><Spinner size="sm" className="me-2" /> Loading…</td></tr>
                            )}
                          </tbody>
                        </Table>
                      </TabPane>
                    ))}
                  </TabContent>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default RecentJobs;
